// SharedSpaceDialog — the ONE unified "Shared Space" member-management dialog
// for Kanban BOARDS and Financial BOOKS (Members UI management #194).
//
// It REPLACES BoardsPanel's ShareDialog and ExpensesPanel's MembersDialog. The
// two surfaces differ only in: the title, the permission copy, the member-row
// testids (kept identical to the old dialogs so the #195 "Invisible UI"
// regression specs stay green), and which service calls run. Everything else —
// the wide container, the white/black initials balls, the real-time
// contact/team picker, the role selector, the info bubble — is shared.
//
// STRICT constraint (the ticket's headline): NO raw-email invites. The picker
// only matches the user's NETWORK contacts (mutual-handshake, linked account)
// and TEAMS; it resolves the pick to a real user id and invites by
// `invitee_user_id`. A typed email that isn't a known network contact adds
// nobody. #195 gating (OG-Creator owns the Owner tier) is preserved verbatim.
//
// #194 Team→Board shares (PO decision, card 229): BOTH surfaces now use the SAME
// team-share substrate — picking a TEAM calls teamService.shareAsset (kind
// 'board' | 'book'), which fans out REAL team_id-stamped membership rows. So:
//   * a team-derived member renders a "via <team>" chip and has NO individual
//     Remove — they're managed by the team share (removing them individually
//     would desynchronize the roster from the team; the BE 409s it too);
//   * the Teams section lists every team shared into this board/book with one
//     Remove that drops all of its members at once (PO point 3), after which
//     re-inviting specific people individually works normally;
//   * an individual add aimed at someone who already has team access is refused
//     by the BE with a teaching 409, surfaced in `shared-space-error`.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, UserMinus, Info, Users, Search, Loader2 } from 'lucide-react';

import boardService from '../services/boardService';
import expenseService from '../services/expenseService';
import teamService from '../services/teamService';
import contactService from '../services/contactService';
import { avatarInitials } from '../utils/avatar';
import { useLanguage } from '../i18n';
import { useDropdownBelowModal } from '../hooks/useDropdownBelowModal';
import Modal from './ui/Modal';
import { userErrorMessage } from '../utils/userError';

// White-bg / black-initials ball — the global profile-avatar standard (#72 New
// Design). Kept solid white in dark mode too (per the card), the border defines
// it in light mode.
const BALL = 'rounded-full bg-white text-pai-void flex items-center justify-center font-bold flex-shrink-0';

const contactEmail = (c) => (c?.omnis_user && c.omnis_user.email) || c?.email || '';

const SURFACES = {
    board: {
        title: 'Share Board',
        rowTestId: 'board-member-row',
        roleTestId: 'member-role-toggle',
        removeTestId: 'member-remove',
        listMembers: (id) => boardService.listMembers(id),
        inviteUser: (id, body) => boardService.inviteMember(id, body),
        updateRole: (id, uid, role) => boardService.updateMember(id, uid, { role }),
        removeMember: (id, uid) => boardService.removeMember(id, uid),
        removeTeamShare: (id, teamId) => boardService.removeTeamShare(id, teamId),
        // #194: a team → board share now uses the REAL team share fan-out (same
        // as books), which mints Member rows — so the role selector is forced to
        // Member for a team pick.
        teamShareKind: 'board',
        teamSupportsRole: false,
        permissions: [
            ['Owner', 'Full board management: add & remove Members, rename the board. An Owner cannot change or remove another Owner.'],
            ['Member', 'Full ticket autonomy: create, edit, move, assign and delete tickets. No member management — the Members list is read-only.'],
            [null, 'Only the Original Creator can delete the board or manage Owner roles.'],
        ],
    },
    book: {
        title: 'Share Book',
        rowTestId: 'book-member-row',
        roleTestId: 'book-member-role',
        removeTestId: 'book-member-remove',
        listMembers: (id) => expenseService.listMembers(id),
        inviteUser: (id, body) => expenseService.addMember(id, body),
        updateRole: (id, uid, role) => expenseService.updateMember(id, uid, { role }),
        removeMember: (id, uid) => expenseService.removeMember(id, uid),
        removeTeamShare: (id, teamId) => expenseService.removeTeamShare(id, teamId),
        // A team → book share uses the EXISTING team book-share fan-out, which
        // fans out Member rows (owner grants gated OG-only server-side) — so the
        // role selector is forced to Member for a team pick here.
        teamShareKind: 'book',
        teamSupportsRole: false,
        permissions: [
            ['Owner', 'Full ledger management: add & remove Members, edit or delete any transaction. An Owner cannot change or remove another Owner.'],
            ['Member', 'Full autonomy over their OWN entries only.'],
            [null, 'Only the Original Creator can delete the book or manage Owner roles.'],
        ],
    },
};

// The `SURFACES` config above is module-level (no `t` hook available there),
// so the localized title + permission-bubble copy is resolved separately,
// inside the component, from these small key tables. `label` reuses the
// existing `teamRoleOwner`/`teamRoleMember` keys ('Owner'/'Member').
const TITLE_I18N = {
    board: ['navShareBoardTitle', 'Share Board'],
    book: ['navShareBookTitle', 'Share Book'],
};
const PERMISSIONS_I18N = {
    board: [
        ['teamRoleOwner', 'Owner', 'navBoardPermOwnerDesc', 'Full board management: add & remove Members, rename the board. An Owner cannot change or remove another Owner.'],
        ['teamRoleMember', 'Member', 'navBoardPermMemberDesc', 'Full ticket autonomy: create, edit, move, assign and delete tickets. No member management — the Members list is read-only.'],
        [null, null, 'navBoardPermNote', 'Only the Original Creator can delete the board or manage Owner roles.'],
    ],
    book: [
        ['teamRoleOwner', 'Owner', 'navBookPermOwnerDesc', 'Full ledger management: add & remove Members, edit or delete any transaction. An Owner cannot change or remove another Owner.'],
        ['teamRoleMember', 'Member', 'navBookPermMemberDesc', 'Full autonomy over their OWN entries only.'],
        [null, null, 'navBookPermNote', 'Only the Original Creator can delete the book or manage Owner roles.'],
    ],
};

const SharedSpaceDialog = ({
    surface,            // 'board' | 'book'
    entityId,           // boardId | bookId
    viewerUserId,
    viewerRole,         // 'owner' | 'member'
    isCreator = false,  // OG Creator (#195)
    onClose,
    onChanged,          // parent refresh hook (board load() / book fetchBooks())
}) => {
    const { t } = useLanguage();
    const cfg = SURFACES[surface];
    const isOwner = viewerRole === 'owner';
    const title = t(...TITLE_I18N[surface]);
    const permissions = PERMISSIONS_I18N[surface].map(
        ([labelKey, labelFallback, textKey, textFallback]) => [
            labelKey ? t(labelKey, labelFallback) : null,
            t(textKey, textFallback),
        ],
    );

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');   // non-error info (e.g. skipped members)

    // Picker state.
    const [contacts, setContacts] = useState([]);   // network-tier only
    const [teams, setTeams] = useState([]);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(null);  // {type:'contact'|'team', id, name, email?}
    const [inviteRole, setInviteRole] = useState('member');
    const [busy, setBusy] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);

    const ownerCount = useMemo(() => members.filter((m) => m.role === 'owner').length, [members]);
    const memberByUserId = useMemo(
        () => new Map(members.map((m) => [m.user_id, m])), [members],
    );

    // #194: the teams currently shared into this board/book, derived from the
    // roster's provenance stamps (team_id/team_name) — no extra request.
    const sharedTeams = useMemo(() => {
        const byId = new Map();
        members.forEach((m) => {
            if (!m.team_id) return;
            const entry = byId.get(m.team_id) || { id: m.team_id, name: m.team_name, count: 0 };
            entry.count += 1;
            byId.set(m.team_id, entry);
        });
        return [...byId.values()];
    }, [members]);
    const sharedTeamIds = useMemo(() => new Set(sharedTeams.map((t) => t.id)), [sharedTeams]);

    const refetchMembers = useCallback(async () => {
        if (!entityId) return;
        setLoading(true);
        try {
            const res = await cfg.listMembers(entityId);
            setMembers(res.members || []);
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, cfg]);

    useEffect(() => { refetchMembers(); }, [refetchMembers]);

    // Load the pickable graph once: NETWORK contacts (linked, so invitable by
    // user id) + the user's teams. Owners only (a plain Member can't invite).
    useEffect(() => {
        if (!isOwner) return;
        let cancelled = false;
        (async () => {
            try {
                const [cRes, tRes] = await Promise.all([
                    contactService.listContacts().catch(() => ({ contacts: [] })),
                    teamService.listTeams().catch(() => ({ teams: [] })),
                ]);
                if (cancelled) return;
                const network = (cRes?.contacts || []).filter(
                    (c) => c.tier === 'network' && c.omnis_user_id,
                );
                setContacts(network);
                setTeams(tRes?.teams || []);
            } catch { /* non-fatal — picker just has fewer options */ }
        })();
        return () => { cancelled = true; };
    }, [isOwner]);

    // A team → book share can only fan out Members (see cfg.teamSupportsRole).
    const teamRoleLocked = selected?.type === 'team' && !cfg.teamSupportsRole;
    useEffect(() => {
        if (teamRoleLocked && inviteRole !== 'member') setInviteRole('member');
    }, [teamRoleLocked, inviteRole]);

    const options = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const contactOpts = contacts
            .filter((c) => {
                if (c.omnis_user_id === viewerUserId) return false;
                const existing = memberByUserId.get(c.omnis_user_id);
                // Already an INDIVIDUAL member → nothing to do, don't offer them.
                // Already a TEAM-derived member → still offered, annotated: the
                // BE is the authority on the refusal, so picking them surfaces the
                // teaching 409 that names the team + the two ways to fix it (#194).
                return !existing || !!existing.team_id;
            })
            .filter((c) => (c.name || '').toLowerCase().includes(q) || contactEmail(c).toLowerCase().includes(q))
            .map((c) => ({
                type: 'contact',
                id: c.omnis_user_id,
                name: c.name || contactEmail(c),
                email: contactEmail(c),
                viaTeam: memberByUserId.get(c.omnis_user_id)?.team_name || null,
            }));
        const teamOpts = teams
            // A team already shared here isn't offered again (the BE 409s a
            // duplicate share) — remove it from the Teams section to re-add.
            .filter((t) => !sharedTeamIds.has(t.id))
            .filter((t) => (t.name || '').toLowerCase().includes(q))
            .map((t) => ({ type: 'team', id: t.id, name: t.name, count: t.member_count }));
        return [...contactOpts, ...teamOpts];
    }, [query, contacts, teams, memberByUserId, viewerUserId, sharedTeamIds]);

    const showDropdown = isOwner && !selected && query.trim().length > 0;

    // The results list is portaled to <body> and positioned BELOW the whole
    // modal box (Share options #363) — see useDropdownBelowModal's docstring
    // for why: absolutely positioned inside the invite footer, it used to
    // grow upward over the member list above it.
    const { modalRef, anchorRef, rect: dropdownRect } = useDropdownBelowModal(showDropdown);

    // Escape closes ONLY this dialog: <Modal> handles it in the capture phase
    // and stops it, so PanelShell's bubble-phase listener never tears down the
    // Boards/Expenses panel behind it (the #225/#272 lesson).

    const pick = (opt) => {
        setSelected(opt);
        setQuery(opt.name);
        setError('');
    };

    const onQueryChange = (e) => {
        setQuery(e.target.value);
        setSelected(null);
    };

    const addLabel = inviteRole === 'owner' ? t('navAddOwner', 'Add Owner') : t('navAddMember', 'Add Member');

    const handleAdd = async () => {
        if (!selected || busy) return;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            if (selected.type === 'contact') {
                await cfg.inviteUser(entityId, { invitee_user_id: selected.id, role: inviteRole });
            } else {
                // #194: ONE substrate for both surfaces — the team share fan-out
                // mints REAL team_id-stamped membership rows, so access stays
                // synchronized with the team roster.
                const res = await teamService.shareAsset(selected.id, {
                    kind: cfg.teamShareKind, assetId: entityId,
                });
                // No dual membership: anyone who already had an INDIVIDUAL grant
                // keeps it and is skipped — say so rather than failing silently.
                const skipped = res?.members_skipped?.length || 0;
                if (skipped) {
                    setNotice(
                        t(
                            'navMembersSkippedNotice',
                            '{n} member{s} already had individual access and kept it — they are not managed by this team share.',
                            { n: skipped, s: skipped === 1 ? '' : 's' },
                        ),
                    );
                }
            }
            setSelected(null);
            setQuery('');
            await refetchMembers();
            onChanged?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    // #194 / PO point 3 — the owner-controlled override: dropping the team drops
    // everyone it granted; specific people are then re-invited individually.
    const handleRemoveTeamShare = async (teamId) => {
        setError('');
        setNotice('');
        setBusy(true);
        try {
            await cfg.removeTeamShare(entityId, teamId);
            await refetchMembers();
            onChanged?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    const handleRoleChange = async (userId, role) => {
        setError('');
        try {
            await cfg.updateRole(entityId, userId, role);
            await refetchMembers();
            onChanged?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    const handleRemove = async (userId) => {
        setError('');
        setNotice('');
        try {
            await cfg.removeMember(entityId, userId);
            const wasSelf = userId === viewerUserId;
            await refetchMembers();
            onChanged?.();
            if (wasSelf) onClose();  // self-leave: we lost access
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
    };

    // Owner option is offered only to the OG Creator (#195). Teams on a book are
    // Member-only.
    const canOfferOwner = isCreator && !teamRoleLocked;

    // <Modal> portals to <body> so the scrim escapes any transformed panel
    // ancestor (the #225 fix) and renders at its true wide size.
    return (
    <>
    <Modal onClose={onClose} zIndex="z-[70]" size="xl" layout="column" cardRef={modalRef} cardClassName="max-h-[85dvh]" scrimTestId="shared-space-dialog">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-5 pb-3 flex-shrink-0">
                    <div>
                        <h2 data-testid="share-dialog-title" className="text-2xl font-bold text-white">
                            {title}
                        </h2>
                        <div data-testid="shared-space-members-label" className="mt-1 text-xs font-semibold uppercase tracking-wide text-pai-faint opacity-70">
                            {t('navMembers', 'Members')}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="share-dialog-close"
                        aria-label={t('commonClose', 'Close')}
                        className="text-pai-faint hover:text-white  p-1 rounded"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Member list — scrolls internally past ~6 rows; invite stays fixed */}
                <div data-testid="shared-space-member-list" className="flex-1 min-h-0 overflow-y-auto px-6 space-y-1.5">
                    {error && (
                        <div data-testid="shared-space-error" className="mb-2 text-xs text-rose-600 dark:text-rose-400">{error}</div>
                    )}
                    {notice && (
                        <div data-testid="shared-space-notice" className="mb-2 text-xs text-amber-600 dark:text-amber-400">{notice}</div>
                    )}
                    {loading && members.length === 0 ? (
                        <p className="text-sm text-pai-meta py-2">{t('navLoadingEllipsis', 'Loading…')}</p>
                    ) : members.map((m) => {
                        const isSelf = m.user_id === viewerUserId;
                        const lastOwner = m.role === 'owner' && ownerCount <= 1;
                        // #194: a TEAM-derived row is managed by the team share —
                        // no individual role change, no individual Remove (the BE
                        // 409s both, this just hides the affordances).
                        const viaTeam = !!m.team_id;
                        // #195 gating — preserved verbatim per surface.
                        const canEditRole = !viaTeam && (surface === 'board'
                            ? (isCreator && !isSelf)
                            : (isCreator && !isSelf && !lastOwner));
                        const canRemove = !viaTeam && (surface === 'board'
                            ? (!isSelf && (isCreator || (isOwner && m.role !== 'owner')))
                            : (!lastOwner && (isSelf || isCreator || (isOwner && m.role !== 'owner'))));
                        return (
                            <div
                                key={m.user_id}
                                data-testid={cfg.rowTestId}
                                data-role={m.role}
                                data-via-team={viaTeam ? 'true' : 'false'}
                                className="flex items-center gap-3 px-2 py-2 rounded-lg bg-white/[0.025] border border-pai-subtle"
                            >
                                <div data-testid="shared-space-member-ball" className={`${BALL} w-9 h-9 text-xs`}>
                                    {avatarInitials({ username: m.username, email: m.email })}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white truncate">
                                        {m.username || m.email}
                                        {isSelf && <span className="ml-1 text-[10px] text-pai-meta">{t('navYouParens', '(you)')}</span>}
                                    </div>
                                    <div className="text-[11px] text-pai-meta truncate">{m.email}</div>
                                </div>
                                {viaTeam && (
                                    <span
                                        data-testid="member-via-team"
                                        data-team-id={m.team_id}
                                        title={t('navAccessViaTeamHint', 'Access comes from a team share — manage it in the Teams section')}
                                        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(74,144,240,0.16)] text-pai-blue bg-[rgba(74,144,240,0.16)] text-pai-blue max-w-[9rem]"
                                    >
                                        <Users size={11} className="flex-shrink-0" />
                                        <span className="truncate">{t('navViaTeam', 'via {team}', { team: m.team_name || t('navTeamFallback', 'team') })}</span>
                                    </span>
                                )}
                                {canEditRole ? (
                                    <select
                                        data-testid={cfg.roleTestId}
                                        value={m.role}
                                        onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                                        className="text-xs px-1.5 py-1 border border-pai rounded bg-white/[0.03] text-pai-secondary"
                                    >
                                        <option value="owner">{t('teamRoleOwner', 'Owner')}</option>
                                        <option value="member">{t('teamRoleMember', 'Member')}</option>
                                    </select>
                                ) : (
                                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                                        m.role === 'owner'
                                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                            : 'bg-white/[0.07] text-pai-secondary bg-white/[0.06] text-pai-secondary'
                                    }`}>
                                        {m.role === 'owner' ? t('navRoleLabelOwner', 'owner') : t('navRoleLabelMember', 'member')}
                                    </span>
                                )}
                                {canRemove && (
                                    <button
                                        data-testid={cfg.removeTestId}
                                        onClick={() => handleRemove(m.user_id)}
                                        className="text-pai-faint hover:text-red-600 dark:hover:text-red-400 p-1 rounded"
                                        title={isSelf ? t('navLeave', 'Leave') : t('navRemoveMember', 'Remove member')}
                                    >
                                        <UserMinus size={16} />
                                    </button>
                                )}
                            </div>
                        );
                    })}

                    {/* Teams sharing this board/book (#194). Removing one revokes
                        every member it granted — the owner-controlled override. */}
                    {sharedTeams.length > 0 && (
                        <div data-testid="shared-space-teams" className="pt-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-pai-faint opacity-70 mb-1.5">
                                {t('tierTeams', 'Teams')}
                            </div>
                            {sharedTeams.map((sharedTeam) => (
                                <div
                                    key={sharedTeam.id}
                                    data-testid="shared-space-team-row"
                                    data-team-id={sharedTeam.id}
                                    className="flex items-center gap-3 px-2 py-2 mb-1.5 rounded-lg bg-[rgba(74,144,240,0.09)] bg-[rgba(74,144,240,0.09)] border border-[rgba(74,144,240,0.35)] "
                                >
                                    <div className={`${BALL} w-9 h-9 text-xs`}><Users size={15} /></div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white truncate">{sharedTeam.name || t('navTeamFallback', 'team')}</div>
                                        <div className="text-[11px] text-pai-meta truncate">
                                            {t('navMembersViaThisTeam', '{n} member{s} via this team', { n: sharedTeam.count, s: sharedTeam.count === 1 ? '' : 's' })}
                                        </div>
                                    </div>
                                    {isOwner && (
                                        <button
                                            data-testid="shared-space-team-remove"
                                            onClick={() => handleRemoveTeamShare(sharedTeam.id)}
                                            disabled={busy}
                                            title={t('navRemoveTeamHint', 'Remove this team — everyone it added loses access')}
                                            className="text-pai-faint hover:text-red-600 dark:hover:text-red-400 p-1 rounded disabled:opacity-40"
                                        >
                                            <UserMinus size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <p className="text-[11px] text-pai-meta mb-1">
                                {t('navRemovingTeamRemovesEveryone', 'Removing a team removes everyone it added. To keep someone, invite them individually afterwards.')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Invite section — fixed below the divider (owners only) */}
                {isOwner && (
                    <div className="flex-shrink-0 border-t border-pai-subtle px-6 py-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-pai-meta mb-2">
                            {t('navInviteContact', 'Invite Contact')}
                        </div>
                        <div className="flex items-end gap-2">
                            {/* Search + real-time dropdown. The results list itself
                                is portaled BELOW the modal (#363) — see the block
                                after this footer. */}
                            <div className="relative flex-1" ref={anchorRef}>
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-pai-faint pointer-events-none" />
                                <input
                                    data-testid="shared-space-search"
                                    type="text"
                                    value={query}
                                    onChange={onQueryChange}
                                    placeholder={t('noteSharePlaceholder', 'Search contact or team...')}
                                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-pai rounded-lg bg-white/[0.03] text-white focus:outline-none focus:ring-2 "
                                />
                            </div>

                            {/* Role selector + info bubble above it */}
                            <div className="flex flex-col items-center gap-1 relative">
                                <button
                                    type="button"
                                    data-testid="shared-space-info"
                                    aria-label={t('navPermissionInfo', 'Permission info')}
                                    // Open on BOTH hover and click; close only on
                                    // mouse-leave. (A click-toggle would fight the
                                    // mouseenter that fires on the same click.)
                                    onClick={() => setInfoOpen(true)}
                                    onMouseEnter={() => setInfoOpen(true)}
                                    onMouseLeave={() => setInfoOpen(false)}
                                    className="text-pai-faint hover:text-pai-blue-tint"
                                >
                                    <Info size={15} />
                                </button>
                                {infoOpen && (
                                    <div
                                        data-testid="shared-space-info-popover"
                                        className="absolute bottom-full right-0 mb-1 w-72 z-20 bg-pai-modal text-white text-[11px] leading-relaxed rounded-lg shadow-modal-sm p-3 space-y-1.5"
                                    >
                                        {permissions.map(([label, text], i) => (
                                            <p key={i}>
                                                {label && <span className="font-semibold">{label}: </span>}
                                                {text}
                                            </p>
                                        ))}
                                    </div>
                                )}
                                <select
                                    data-testid="shared-space-role"
                                    value={inviteRole}
                                    disabled={teamRoleLocked}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                    className="text-sm px-2 py-2.5 border border-pai rounded-lg bg-white/[0.03] text-pai-secondary disabled:opacity-50"
                                >
                                    <option value="member">{t('teamRoleMember', 'Member')}</option>
                                    {canOfferOwner && <option value="owner">{t('teamRoleOwner', 'Owner')}</option>}
                                </select>
                            </div>

                            {/* Dynamic Add button */}
                            <button
                                type="button"
                                data-testid="shared-space-add"
                                disabled={!selected || busy}
                                onClick={handleAdd}
                                className="px-4 py-2.5 text-sm font-medium bg-white text-pai-void hover:bg-[rgba(74,144,240,0.12)]0 disabled:bg-white/30  disabled:cursor-not-allowed rounded-lg whitespace-nowrap flex items-center gap-1.5"
                            >
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                {addLabel}
                            </button>
                        </div>
                        {teamRoleLocked && (
                            <p data-testid="shared-space-team-note" className="mt-1.5 text-[11px] text-pai-meta">
                                {t('navTeamsAddedAsMembers', 'Teams are added as Members, and their access follows the team roster.')}
                            </p>
                        )}
                    </div>
                )}
        </Modal>
    {/* Results list — anchored to the search input horizontally, but always
        rendered in the space BELOW the whole modal box (#363), never layered
        over the modal's own member list/labels. A separate <body> portal so
        it escapes the modal's own stacking/clipping entirely. */}
    {showDropdown && dropdownRect && createPortal(
        <div
            data-testid="shared-space-dropdown"
            style={{
                position: 'fixed',
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
                maxHeight: Math.min(dropdownRect.maxHeight, 240),
            }}
            className="z-[75] overflow-y-auto bg-pai-modal border border-pai rounded-lg shadow-modal-sm py-1"
        >
            {options.length === 0 ? (
                <div data-testid="shared-space-no-results" className="px-3 py-2 text-xs text-pai-meta">
                    {t('noteShareNoResults', 'No contacts or teams match. Connect in Contacts first.')}
                </div>
            ) : options.map((opt) => (
                <button
                    key={`${opt.type}:${opt.id}`}
                    type="button"
                    data-testid="shared-space-option"
                    data-option-type={opt.type}
                    onClick={() => pick(opt)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[rgba(74,144,240,0.12)] dark:hover:bg-[rgba(74,144,240,0.12)]"
                >
                    {opt.type === 'team' ? (
                        <span className={`${BALL} w-7 h-7 text-[10px]`}><Users size={13} /></span>
                    ) : (
                        <span className={`${BALL} w-7 h-7 text-[10px]`}>{avatarInitials({ username: opt.name, email: opt.email })}</span>
                    )}
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white truncate">{opt.name}</span>
                        <span className="block text-[11px] text-pai-meta truncate">
                            {opt.type === 'team' ? t('navTeamMemberCount', 'Team · {n} members', { n: opt.count ?? 0 }) : opt.email}
                        </span>
                        {opt.viaTeam && (
                            <span
                                data-testid="shared-space-option-via-team"
                                className="block text-[11px] text-pai-blue text-pai-blue truncate"
                            >
                                {t('navAlreadyHereViaTeam', 'Already here via {team}', { team: opt.viaTeam })}
                            </span>
                        )}
                    </span>
                </button>
            ))}
        </div>,
        document.body,
    )}
    </>
    );
};

export default SharedSpaceDialog;
